import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import axios from 'axios'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Button } from 'components/ui/button'
import { Alert } from 'components/ui/alert'
import { Tabs, TabsList, TabsTrigger, TabsContent } from 'components/ui/tabs'
import { SingleImageUploader } from 'components/FileUploader'
import { Loader2, Eye, EyeOff, RefreshCw, Linkedin, Check, X } from 'lucide-react'
import { patchUser } from 'slices/authSlice'
import { advanceOnboardingStep, ONBOARDING_STEPS } from 'slices/onboardingSlice'
import { withAuth } from 'utils/authHeaders'

interface ProfileStepProps {
	user: any
	onSkip: () => void
}

const ROLE_OPTIONS = [
	'Broker',
	'Landlord',
	'Developer',
	'Contractor',
	'Property Manager',
	'Tenant Rep',
	'Asset Manager',
	'Other',
]

// Password validation rules
const PASSWORD_RULES = {
	minLength: 8,
	hasUppercase: /[A-Z]/,
	hasNumber: /[0-9]/,
	hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/,
}

function validatePassword(pwd: string) {
	return {
		minLength: pwd.length >= PASSWORD_RULES.minLength,
		hasUppercase: PASSWORD_RULES.hasUppercase.test(pwd),
		hasNumber: PASSWORD_RULES.hasNumber.test(pwd),
		hasSpecial: PASSWORD_RULES.hasSpecial.test(pwd),
	}
}

function isPasswordValid(pwd: string): boolean {
	const v = validatePassword(pwd)
	return v.minLength && v.hasUppercase && v.hasNumber && v.hasSpecial
}

function generateStrongPassword(): string {
	const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
	const lower = 'abcdefghjkmnpqrstuvwxyz'
	const numbers = '23456789'
	const specials = '!@#$%&*?'
	const all = upper + lower + numbers + specials

	// Ensure at least one of each required type
	let pwd = ''
	pwd += upper[Math.floor(Math.random() * upper.length)]
	pwd += lower[Math.floor(Math.random() * lower.length)]
	pwd += numbers[Math.floor(Math.random() * numbers.length)]
	pwd += specials[Math.floor(Math.random() * specials.length)]

	// Fill remaining 8 chars
	for (let i = 0; i < 8; i++) {
		pwd += all[Math.floor(Math.random() * all.length)]
	}

	// Shuffle
	return pwd
		.split('')
		.sort(() => Math.random() - 0.5)
		.join('')
}

const PasswordRule: React.FC<{ met: boolean; label: string }> = ({ met, label }) => (
	<div className="flex items-center gap-1.5 text-xs">
		{met ? (
			<Check className="w-3 h-3 text-green-500 flex-shrink-0" />
		) : (
			<X className="w-3 h-3 text-gray-300 flex-shrink-0" />
		)}
		<span className={met ? 'text-green-600' : 'text-gray-400'}>{label}</span>
	</div>
)

const ProfileStep: React.FC<ProfileStepProps> = ({ user }) => {
	const dispatch = useDispatch()
	const { saving } = useSelector((state: any) => state.onboarding)

	const [formData, setFormData] = useState({
		name: user.name || '',
		firstName: user.firstName || '',
		company: user.company || '',
		title: user.title || '',
		role: user.role || '',
		phone: user.phone || '',
		linkedin: user.linkedin || '',
		image: user.image || '',
	})

	const [password, setPassword] = useState('')
	const [confirmPassword, setConfirmPassword] = useState('')
	const [showPassword, setShowPassword] = useState(false)
	const [error, setError] = useState('')
	const [activeTab, setActiveTab] = useState('about')

	// LinkedIn image fetching state
	const [linkedinPromptShown, setLinkedinPromptShown] = useState(false)
	const [linkedinFetching, setLinkedinFetching] = useState(false)
	const [linkedinMessage, setLinkedinMessage] = useState('')

	const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		const { name, value } = e.target
		setFormData(prev => ({ ...prev, [name]: value }))

		// When LinkedIn URL changes and has content, show the prompt
		if (name === 'linkedin' && value.trim() && value.includes('linkedin.com')) {
			setLinkedinPromptShown(true)
			setLinkedinMessage('')
		}
	}

	const handleImageUpload = async (fileInfo: any) => {
		const fileUrl = fileInfo?.originalUrl ?? fileInfo?.cdnUrl ?? ''
		if (!fileUrl) return

		setFormData(prev => ({ ...prev, image: fileUrl }))
		await dispatch(
			patchUser({
				userid: user._id,
				updates: { image: fileUrl },
			}),
		)
	}

	const fetchLinkedInImage = async () => {
		if (!formData.linkedin.trim()) return

		setLinkedinFetching(true)
		setLinkedinMessage('')

		try {
			const res = await axios.post(
				'/api/users/linkedin-image',
				{ linkedinUrl: formData.linkedin },
				withAuth(),
			)

			if (res.data.success && res.data.imageUrl) {
				setFormData(prev => ({ ...prev, image: res.data.imageUrl }))
				await dispatch(
					patchUser({
						userid: user._id,
						updates: { image: res.data.imageUrl },
					}),
				)
				setLinkedinMessage('LinkedIn photo imported successfully!')
				setLinkedinPromptShown(false)
			} else {
				setLinkedinMessage(
					res.data.message || 'Could not fetch LinkedIn photo. Please upload manually.',
				)
			}
		} catch (err) {
			setLinkedinMessage('Could not fetch LinkedIn photo. Please upload manually.')
		} finally {
			setLinkedinFetching(false)
		}
	}

	const handleGeneratePassword = () => {
		const generated = generateStrongPassword()
		setPassword(generated)
		setConfirmPassword(generated)
		setShowPassword(true) // Show it so user can copy it
	}

	// --- Validation ---
	const isCompanyValid = formData.company.trim() !== ''
	const passwordChecks = validatePassword(password)
	const isPasswordGood = isPasswordValid(password)
	const passwordsMatch = password === confirmPassword && confirmPassword !== ''

	// About You tab is complete when company is filled
	const isAboutComplete = isCompanyValid

	// Profile & Connect is complete when password is set and valid
	const isConnectComplete = isPasswordGood && passwordsMatch

	// Both tabs must be complete
	const canProceed = isAboutComplete && isConnectComplete

	const handleNext = async () => {
		setError('')

		if (!isCompanyValid) {
			setError('Company name is required.')
			setActiveTab('about')
			return
		}

		if (!isPasswordGood) {
			setError('Password does not meet the requirements.')
			setActiveTab('connect')
			return
		}

		if (!passwordsMatch) {
			setError('Passwords do not match.')
			setActiveTab('connect')
			return
		}

		const updates: any = {
			name: formData.name,
			firstName: formData.firstName,
			company: formData.company,
			title: formData.title,
			role: formData.role,
			phone: formData.phone,
			linkedin: formData.linkedin,
			image: formData.image,
			password: password,
		}

		await dispatch(patchUser({ userid: user._id, updates }))
		dispatch(
			advanceOnboardingStep({
				nextStep: ONBOARDING_STEPS.LOCATION,
				userId: user._id,
			}),
		)
	}

	const handleBack = () => {
		dispatch(
			advanceOnboardingStep({
				nextStep: ONBOARDING_STEPS.WELCOME,
				userId: user._id,
			}),
		)
	}

	return (
		<div className="p-6">
			<h2 className="text-lg font-semibold text-[#074f9b] mb-1">Complete Your Profile</h2>
			<p className="text-sm text-gray-500 mb-4">
				Tell us about yourself so your team can recognize you.
			</p>

			{error && (
				<Alert variant="danger" className="mb-4">
					{error}
				</Alert>
			)}

			<Tabs value={activeTab} onValueChange={setActiveTab}>
				<TabsList className="w-full mb-4">
					<TabsTrigger value="about" className="flex-1">
						About You
						{isAboutComplete && <Check className="w-3 h-3 ml-1.5 text-green-500" />}
					</TabsTrigger>
					<TabsTrigger value="connect" className="flex-1">
						Profile & Connect
						{isConnectComplete && <Check className="w-3 h-3 ml-1.5 text-green-500" />}
					</TabsTrigger>
				</TabsList>

				<TabsContent value="about">
					<div className="space-y-3">
						<div className="grid grid-cols-2 gap-3">
							<div className="space-y-1">
								<Label htmlFor="name">Full Name</Label>
								<Input
									id="name"
									name="name"
									value={formData.name}
									onChange={handleChange}
									className="h-9"
								/>
							</div>
							<div className="space-y-1">
								<Label htmlFor="firstName">First Name</Label>
								<Input
									id="firstName"
									name="firstName"
									value={formData.firstName}
									onChange={handleChange}
									className="h-9"
								/>
							</div>
						</div>

						<div className="space-y-1">
							<Label>Email</Label>
							<Input value={user.email} disabled className="h-9 bg-gray-50 text-gray-500" />
						</div>

						<div className="space-y-1">
							<Label htmlFor="company">
								Company Name <span className="text-red-500">*</span>
							</Label>
							<Input
								id="company"
								name="company"
								value={formData.company}
								onChange={handleChange}
								placeholder="Enter your company name"
								className="h-9"
							/>
						</div>

						<div className="grid grid-cols-2 gap-3">
							<div className="space-y-1">
								<Label htmlFor="title">Title</Label>
								<Input
									id="title"
									name="title"
									value={formData.title}
									onChange={handleChange}
									placeholder="e.g. VP of Real Estate"
									className="h-9"
								/>
							</div>
							<div className="space-y-1">
								<Label htmlFor="role">Role</Label>
								<select
									id="role"
									name="role"
									title="Role"
									aria-label="Role"
									value={formData.role}
									onChange={handleChange}
									className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
								>
									<option value="">Select a role</option>
									{ROLE_OPTIONS.map(role => (
										<option key={role} value={role}>
											{role}
										</option>
									))}
								</select>
							</div>
						</div>
					</div>
				</TabsContent>

				<TabsContent value="connect">
					<div className="space-y-4">
						{/* Password section — required */}
						<div className="border rounded-lg p-3 space-y-3">
							<div className="flex items-center justify-between">
								<Label className="text-sm font-semibold">
									Set Your Password <span className="text-red-500">*</span>
								</Label>
								<Button
									type="button"
									variant="ghost"
									onClick={handleGeneratePassword}
									className="text-xs h-7 px-2"
								>
									<RefreshCw className="w-3 h-3" />
									Generate Strong Password
								</Button>
							</div>

							<div className="space-y-1">
								<Label htmlFor="onb-password" className="text-xs font-normal">
									Password
								</Label>
								<div className="relative">
									<Input
										id="onb-password"
										type={showPassword ? 'text' : 'password'}
										value={password}
										onChange={e => setPassword(e.target.value)}
										placeholder="Enter a strong password"
										className="h-9 pr-9"
									/>
									<button
										type="button"
										onClick={() => setShowPassword(!showPassword)}
										className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
									>
										{showPassword ? (
											<EyeOff className="w-4 h-4" />
										) : (
											<Eye className="w-4 h-4" />
										)}
									</button>
								</div>
							</div>

							<div className="space-y-1">
								<Label htmlFor="onb-confirmPassword" className="text-xs font-normal">
									Confirm Password
								</Label>
								<Input
									id="onb-confirmPassword"
									type={showPassword ? 'text' : 'password'}
									value={confirmPassword}
									onChange={e => setConfirmPassword(e.target.value)}
									placeholder="Re-enter your password"
									className="h-9"
								/>
								{confirmPassword && !passwordsMatch && (
									<p className="text-xs text-red-500 mt-0.5">Passwords do not match</p>
								)}
							</div>

							{/* Password strength rules */}
							{password && (
								<div className="grid grid-cols-2 gap-1 pt-1">
									<PasswordRule
										met={passwordChecks.minLength}
										label="At least 8 characters"
									/>
									<PasswordRule
										met={passwordChecks.hasUppercase}
										label="One uppercase letter"
									/>
									<PasswordRule met={passwordChecks.hasNumber} label="One number" />
									<PasswordRule
										met={passwordChecks.hasSpecial}
										label="One special character"
									/>
								</div>
							)}
						</div>

						{/* Phone */}
						<div className="space-y-1">
							<Label htmlFor="phone">Phone Number</Label>
							<Input
								id="phone"
								name="phone"
								type="tel"
								value={formData.phone}
								onChange={handleChange}
								placeholder="(555) 123-4567"
								className="h-9"
							/>
						</div>

						{/* LinkedIn */}
						<div className="space-y-1">
							<Label htmlFor="linkedin">LinkedIn URL</Label>
							<Input
								id="linkedin"
								name="linkedin"
								value={formData.linkedin}
								onChange={handleChange}
								placeholder="https://linkedin.com/in/yourprofile"
								className="h-9"
							/>

							{/* LinkedIn image prompt */}
							{linkedinPromptShown &&
								formData.linkedin.includes('linkedin.com') &&
								!formData.image && (
									<div className="flex items-center gap-2 p-2 mt-1 bg-blue-50 rounded-md border border-blue-100">
										<Linkedin className="w-4 h-4 text-[#0077b5] flex-shrink-0" />
										<p className="text-xs text-gray-700 flex-1">
											Would you like to use your LinkedIn photo as your profile picture?
										</p>
										<Button
											type="button"
											variant="ghost"
											onClick={fetchLinkedInImage}
											disabled={linkedinFetching}
											className="text-xs h-7 px-2"
										>
											{linkedinFetching ? (
												<Loader2 className="w-3 h-3 animate-spin" />
											) : (
												'Yes, import it'
											)}
										</Button>
										<Button
											type="button"
											variant="ghost"
											onClick={() => setLinkedinPromptShown(false)}
											className="text-xs h-7 px-1"
										>
											No
										</Button>
									</div>
								)}

							{linkedinMessage && (
								<p
									className={`text-xs mt-1 ${
										linkedinMessage.includes('success')
											? 'text-green-600'
											: 'text-amber-600'
									}`}
								>
									{linkedinMessage}
								</p>
							)}
						</div>

						{/* Profile picture */}
						<div className="space-y-1">
							<Label>Profile Picture</Label>
							<div className="flex justify-center">
								<SingleImageUploader
									value={formData.image}
									onChange={handleImageUpload}
									size={160}
									description={
										!isCompanyValid
											? 'Enter a company name first to enable upload'
											: 'Drag & drop or click to upload your photo'
									}
									label="Profile Photo"
									disabled={!isCompanyValid}
								/>
							</div>
						</div>
					</div>
				</TabsContent>
			</Tabs>

			<div className="flex gap-2 mt-6">
				<Button variant="ghost" onClick={handleBack} disabled={saving} className="flex-1">
					Back
				</Button>
				<Button onClick={handleNext} disabled={saving || !canProceed} className="flex-1">
					{saving ? (
						<>
							<Loader2 className="w-4 h-4 animate-spin" />
							Saving...
						</>
					) : (
						'Next'
					)}
				</Button>
			</div>
			{!canProceed && (
				<p className="text-xs text-center text-gray-400 mt-2">
					Complete both tabs to continue
				</p>
			)}
		</div>
	)
}

export default ProfileStep
